import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import CentralizedSpinner from 'components/CentralizedSpinner';
import Button from 'components/DesignSystem/Button';
import MobileSectionHeader from 'components/Layout/MobileShell/MobileSectionHeader';
import MoreSheet from 'components/Layout/MobileShell/MoreSheet';
import useInstructionPdf from 'hooks/Flux/About/useInstructionPdf';
import { resolveAboutTabKey } from 'helper/flux/about';
import { InstructionTab } from 'types/Flux/About/getInstructionTabsResponse';

import './index.scss';

// Mesmo <object> inline do desktop (AboutSection), só que em uma caixa de altura fixa —
// em vez de 85vh, que no mobile empurraria o botão de tela cheia para fora da viewport.
function AboutMobilePdfCard({ tab }: { tab: InstructionTab }) {
  const { pdfUrl, loading } = useInstructionPdf(tab.id, true);

  return (
    <div className="flux-about-mobile-card">
      <p className="flux-about-mobile-card-title">{tab.title}</p>

      <div className="flux-about-mobile-card-preview">
        {loading && <CentralizedSpinner />}
        {!loading && pdfUrl && (
          <object
            title={`${tab.title} PDF`}
            data={pdfUrl}
            className="flux-about-mobile-pdf"
          />
        )}
      </div>

      <Button
        icon="open_in_new"
        iconPlacement="trailing"
        disabled={!pdfUrl}
        small
        onClick={() => window.open(pdfUrl, '_blank', 'noopener,noreferrer')}
      >
        Abrir em tela cheia
      </Button>
    </div>
  );
}

export default function AboutMobile({
  instructionTabs,
  isLoading,
}: {
  instructionTabs?: InstructionTab[];
  isLoading: boolean;
}) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [activeId, setActiveId] = useState<number | null>(null);
  const [moreOpen, setMoreOpen] = useState(false);

  // `?tab=` (id ou slug do titulo) abre a aba pedida, igual ao desktop — e o
  // que faz o link do guia da tela de upload cair direto em Estudos Upload.
  const requestedKey = resolveAboutTabKey(instructionTabs, searchParams.get('tab'));
  const defaultId = requestedKey ? Number(requestedKey) : instructionTabs?.[0]?.id;

  const activeTab = instructionTabs?.find((tab) => tab.id === (activeId ?? defaultId));

  return (
    <div className="flux-about-mobile">
      <MobileSectionHeader
        backLabel="Flux"
        title="Sobre"
        onBack={() => navigate('/flux')}
        onAvatarClick={() => setMoreOpen(true)}
      />

      {isLoading && <CentralizedSpinner />}

      {!isLoading && !!instructionTabs?.length && (
        <>
          <div className="flux-about-mobile-tabs">
            {instructionTabs.map((tab) => (
              <button
                key={tab.id}
                type="button"
                className={`flux-about-mobile-tab${tab.id === activeTab?.id ? ' active' : ''}`}
                onClick={() => setActiveId(tab.id)}
              >
                {tab.title.toUpperCase()}
              </button>
            ))}
          </div>

          {activeTab && <AboutMobilePdfCard key={activeTab.id} tab={activeTab} />}
        </>
      )}

      <MoreSheet open={moreOpen} dismiss={() => setMoreOpen(false)} />
    </div>
  );
}
